import { useCallback, useEffect } from 'react';

const ZOOM_IN_KEYS = new Set(['+', '=', 'Add']);
const ZOOM_OUT_KEYS = new Set(['-', '_', 'Subtract']);
const ZOOM_STEP = 1;

const useMapKeyboardZoom = (
  mapInstance,
  showInfoWindow,
  onZoomOut,
  { onLocate, onReset } = {}
) => {
  const handleKeyboardZoom = useCallback(
    (event) => {
      if (!mapInstance) {
        return;
      }

      const { key } = event;
      const isZoomIn = ZOOM_IN_KEYS.has(key);
      const isZoomOut = ZOOM_OUT_KEYS.has(key);

      if (!isZoomIn && !isZoomOut) {
        return;
      }

      event.preventDefault();

      const diff = isZoomIn ? ZOOM_STEP : -ZOOM_STEP;
      const nextZoom = mapInstance.getZoom() + diff;

      mapInstance.zoomTo(nextZoom, { duration: 300 });

      if (diff < 0 && showInfoWindow) {
        onZoomOut?.();
      }
    },
    [mapInstance, showInfoWindow, onZoomOut]
  );

  useEffect(() => {
    if (!mapInstance) {
      return undefined;
    }

    const mapCanvas = mapInstance.getCanvas?.();
    if (!mapCanvas) {
      return undefined;
    }

    mapCanvas.setAttribute('tabindex', '0');
    mapCanvas.addEventListener('keydown', handleKeyboardZoom);

    return () => {
      mapCanvas.removeEventListener('keydown', handleKeyboardZoom);
    };
  }, [mapInstance, handleKeyboardZoom]);

  useEffect(() => {
    if (!onLocate && !onReset) {
      return undefined;
    }

    const handleGlobalShortcuts = (event) => {
      const { key, target } = event;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable = target?.isContentEditable;
      if (
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        isEditable
      ) {
        return;
      }

      if ((key === 'l' || key === 'L') && onLocate) {
        event.preventDefault();
        onLocate();
        return;
      }

      if ((key === 'r' || key === 'R') && onReset) {
        event.preventDefault();
        onReset();
      }
    };

    window.addEventListener('keydown', handleGlobalShortcuts);
    return () => window.removeEventListener('keydown', handleGlobalShortcuts);
  }, [onLocate, onReset]);
};

export default useMapKeyboardZoom;
